import {
  urlForConversationWithID,
  urlForConversationWithServerID,
  useConversationIdFromSearchParams,
  useUrlNormalization,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useWorkflowAgentDetails } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/useWorkflowAgentDetails";
import { useStackChatDetails } from "@mirage/mosaics/ConversationAssistant/components/stack-chat/useStackChatDetails";
import { useChatRoute } from "@mirage/mosaics/ConversationAssistant/hooks/useChatRoute";
import { useWorkflowAgentDeps } from "@mirage/mosaics/ConversationAssistant/hooks/useWorkflowAgentDeps";
import { createNewConversation } from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { useWorkflowAgent } from "@mirage/service-conversation/hooks/useWorkflowAgents";
import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import type {
  Conversation,
  WorkflowAgent,
} from "@mirage/service-conversation/types";
import { usePrevious } from "@mirage/shared/util/hooks";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback, useEffect, useState } from "react";
import type { Location } from "react-router-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/**
 * Hook to handle listening to routing changes and updating the conversation state; as well as
 * logic for navigating away from the current conversation.
 */
export function useRouteHandling(
  variant: "default" | "condensed" | "workflow-agent-preview",
  onConversationIdChange?: (conversationId: string | undefined) => void,
  previewWorkflowAgent?: WorkflowAgent | undefined,
  shouldUseRouterNavigate?: boolean,
) {
  // Fix URL normalization issue on staging.dash.ai
  useUrlNormalization();

  const chatRoute = useChatRoute();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { conversationId, resetNewConversation, isConversationLoading } =
    useConversationId(searchParams);
  const conversation = useConversation(conversationId);

  useEffect(() => {
    onConversationIdChange?.(conversationId);
  }, [conversationId, onConversationIdChange]);

  const stackChatDetails = useStackChatDetails(state, conversation);
  const workflowAgentConfig = useWorkflowAgentConfig(
    state,
    conversation,
    variant === "workflow-agent-preview",
    previewWorkflowAgent,
  );

  // Update URL when conversation gets a server ID assigned
  const prevServerConversationId = usePrevious(
    conversation?.serverConversationId,
  );
  useEffect(() => {
    if (
      conversation?.serverConversationId &&
      !prevServerConversationId &&
      !searchParams.get("serverConversationId") &&
      variant !== "workflow-agent-preview"
    ) {
      const newUrl = urlForConversationWithServerID(
        conversation.serverConversationId,
        chatRoute,
      );
      if (shouldUseRouterNavigate) {
        navigate(newUrl, { state });
      } else {
        // Update URL without triggering a re-render
        window.history.replaceState(state, "", newUrl);
      }
    }
  }, [
    conversation?.serverConversationId,
    prevServerConversationId,
    conversationId,
    searchParams,
    chatRoute,
    state,
    variant,
    navigate,
    shouldUseRouterNavigate,
  ]);
  const scenarioMeta = state?.scenarioMeta;

  const navigateNewConversation = useCallback(
    (routerState?: ConversationRouterState) => {
      resetNewConversation(() => {
        navigate(chatRoute, { replace: true, state: routerState });
      });
    },
    [resetNewConversation, chatRoute, navigate],
  );

  const navigateNewWorkflowAgent = useCallback(() => {
    navigate(RoutePath.NEW_WORKFLOW_AGENT);
  }, [navigate]);

  return {
    conversationId,
    conversation,
    conversationType: state?.conversationType,
    isConversationLoading,
    stackChatDetails,
    workflowAgentConfig,
    scenarioMeta,
    navigateNewConversation,
    navigateNewWorkflowAgent,
  };
}

/**
 * Hook to handle tracking workflow agent state against the current route
 */
export function useWorkflowAgentConfig(
  state: Location["state"],
  conversation: Conversation | undefined,
  showPreview: boolean,
  previewWorkflowAgent: WorkflowAgent | undefined,
) {
  const workflowAgentDetails = useWorkflowAgentDetails(state, conversation);
  const workflowAgentId = showPreview
    ? "preview"
    : workflowAgentDetails?.agentId;
  const workflowAgentFromHook = useWorkflowAgent(workflowAgentId);
  const workflowAgent =
    workflowAgentId === "preview"
      ? previewWorkflowAgent
      : workflowAgentFromHook;
  const workflowAgentFields = useWorkflowAgentDeps(workflowAgent);
  return {
    workflowAgent,
    workflowAgentFields,
    model: workflowAgentDetails?.model,
  };
}

/**
 * Hook to handle tracking conversation state against the current route
 */
function useConversationId(searchParams: URLSearchParams) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const chatRoute = useChatRoute();

  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const conversationIdLookupFromSearchParams =
    useConversationIdFromSearchParams(searchParams);

  const resetNewConversation = useCallback(
    (onResetComplete?: () => void) => {
      createNewConversation()
        .then((conversation) => {
          // If a new conversation was created as the result of a stateful navigation, update
          // the URL to point to the newly created conversation ID, replacing the current entry
          // and persisting the router state.
          if (state && "conversationType" in state) {
            navigate(urlForConversationWithID(conversation.id, chatRoute), {
              replace: true,
              state,
            });
          }

          setConversationId(conversation.id);

          onResetComplete?.();

          return;
        })
        .catch((e) => {
          throw e;
        });
    },
    [navigate, setConversationId, chatRoute],
  );

  useEffect(() => {
    if (conversationIdLookupFromSearchParams.state === "loaded") {
      if (conversationIdLookupFromSearchParams.conversationId) {
        setConversationId(conversationIdLookupFromSearchParams.conversationId);
      } else {
        // If there is no conversation id in the search params only if we don't already
        // have a conversation id from another source. This prevents duplicate resets after
        // an explicit call to resetNewConversation.
        if (!conversationId) {
          resetNewConversation();
        }
      }
    }
  }, [conversationIdLookupFromSearchParams, resetNewConversation]);
  return {
    conversationId,
    resetNewConversation,
    isConversationLoading:
      conversationIdLookupFromSearchParams.state === "loading",
  };
}
